import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import {
    AccreditationDemand,
    AccreditationDemandFilters,
    ReviewDemandPayload,
    ReviewDemandResponse,
} from "../types/media";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { REVIEW_DEMAND_INVALIDATES } from "./mediaInvalidation";
import { mediaErrorFromBody, mediaErrorFromInvoke } from "./mediaInvokeError";
import { algiersToday, keepRowForDeletedHolder } from "./mediaDeletedLogic";
import {
    ACTIVE_REQUEST_STATUSES,
    perMatchQuota,
    type PerMatchQuota,
} from "./accreditationDemandLogic";

// Pure row helpers live in their own module (node-testable, no Supabase
// client) and are re-exported here so consumers have a single import site.
export {
    demandRequestCounts,
    demandKickoffIso,
    demandKickoffMs,
    isStalePendingDemand,
    isActionableDemand,
    countActionableDemands,
    demandDisplayStatus,
    demandUrgencyRank,
    MATCH_PASSED_STATUS,
    demandDecision,
    perMatchQuota,
    initialRowZones,
    buildReviewOverrides,
    MATCH_ORG_CAP,
} from "./accreditationDemandLogic";
export type {
    DemandRequestCounts,
    DemandDecision,
    PerMatchQuota,
} from "./accreditationDemandLogic";

/**
 * Fallback when a league has no explicit `accreditation_cutoff_hours`
 * (mirrors the column default on `leagues`).
 */
export const DEFAULT_ACCREDITATION_CUTOFF_HOURS = 1;

// ============================================================================
// Accreditation demands data layer (federation / admin webapp)
// Mirrors useAccreditationRequests.ts. Reads go direct-client (admins have
// RLS via accreditation_demands_admin_all); only the review action is an
// edge function (service role, admin-verified, reuses the exact per-request
// approval side-effects of review-accreditation-request).
// ============================================================================

interface UseAccreditationDemandsOptions {
    filters?: AccreditationDemandFilters;
    enabled?: boolean;
}

const DEMAND_MATCH_SELECT = `
    id,
    status,
    match_date,
    match_time,
    game_day,
    home_team:teams!home_team_id (name, name_ar),
    away_team:teams!away_team_id (name, name_ar),
    stadium:stadiums!stadium_id (
      name,
      name_ar,
      location_id,
      location:locations!fk_stadiums_location (
        wilaya,
        wilaya_ar,
        commune:communes,
        commune_ar
      )
    )
  `;

const DEMAND_LIST_SELECT = `
    *,
    organization:media_organizations!organization_id (
      id,
      name,
      name_ar,
      logo_url,
      account_kind,
      team_id,
      deleted_at,
      team:teams!team_id (id, name, name_ar)
    ),
    match:matches!match_id (
      ${DEMAND_MATCH_SELECT}
    ),
    requests:accreditation_requests!demand_id (
      id,
      status
    )
  `;

const DEMAND_DETAIL_SELECT = `
    *,
    organization:media_organizations!organization_id (
      id,
      name,
      name_ar,
      org_type,
      logo_url,
      account_kind,
      team_id,
      deleted_at,
      team:teams!team_id (id, name, name_ar)
    ),
    match:matches!match_id (
      ${DEMAND_MATCH_SELECT}
    ),
    league:leagues!league_id (
      name,
      name_ar
    ),
    requests:accreditation_requests!demand_id (
      id,
      status,
      rejection_reason,
      badge_issued_at,
      badge_serial,
      valid_until,
      requested_zones,
      approved_zones,
      media_personnel:media_personnel_id (
        id,
        user_id,
        media_type,
        photo_url,
        is_verified,
        lirf_status,
        participation_suspended,
        rejection_reason,
        organization_id,
        deleted_at,
        first_name,
        last_name,
        first_name_ar,
        last_name_ar,
        profile:profiles!profile_id (
          id,
          full_name,
          avatar_url
        )
      )
    )
  `;

/**
 * Resolve the reviewer display names of a batch of demands.
 *
 * `accreditation_demands.reviewed_by` references `auth.users(id)` — not
 * `profiles` — so PostgREST has no embed path and the names need one secondary
 * select (same motif as `useLeagueAccessRequests`). Best-effort on purpose: a
 * failed lookup leaves `reviewer_name` null («—» in the UI) and must never
 * fail the list.
 */
async function attachReviewerNames(
    demands: AccreditationDemand[]
): Promise<AccreditationDemand[]> {
    const reviewerIds = [
        ...new Set(
            demands
                .map((d) => d.reviewed_by)
                .filter((id): id is string => !!id)
        ),
    ];
    if (reviewerIds.length === 0) return demands;

    const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", reviewerIds);
    const nameById = new Map(
        (profiles ?? []).map((p: any) => [p.id, p.full_name])
    );
    for (const demand of demands) {
        demand.reviewer_name = demand.reviewed_by
            ? nameById.get(demand.reviewed_by) ?? null
            : null;
    }
    return demands;
}

/**
 * Hook to fetch accreditation demands with filters.
 * Scoped by the global league and season selection (mirrors useAccreditationRequests).
 */
export function useAccreditationDemands(
    options: UseAccreditationDemandsOptions = {}
) {
    const { filters = {}, enabled = true } = options;
    const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId);
    const currentSeason = useLeagueSeasonStore((state) => state.currentSeason);

    return useQuery<AccreditationDemand[]>({
        queryKey: ["accreditation-demands", currentLeagueId, currentSeason, filters],
        queryFn: async () => {
            let query = supabase
                .from("accreditation_demands")
                .select(DEMAND_LIST_SELECT)
                .order("created_at", { ascending: false });

            if (currentLeagueId) {
                query = query.eq("league_id", currentLeagueId);
            }

            if (currentSeason) {
                query = query.eq("season", currentSeason);
            }

            if (filters.status) {
                query = query.eq("status", filters.status);
            }

            const { data, error } = await query;

            if (error) {
                throw new Error(error.message);
            }

            // A deleted organisme keeps only its demands of played matches
            // (any status); its cancelled upcoming ones leave the list.
            const today = algiersToday();
            const demands = (
                (data || []) as unknown as AccreditationDemand[]
            ).filter((d) => keepRowForDeletedHolder(d.match, [d.organization], today));
            return attachReviewerNames(demands);
        },
        enabled: enabled && !!currentLeagueId,
        staleTime: 30000, // 30 seconds
    });
}

/**
 * Accreditation cut-off (in hours before kick-off) of the currently selected
 * league. Fetched once per view: the value is league configuration, so it is
 * cached aggressively and shared by every demand row.
 */
export function useLeagueAccreditationCutoffHours() {
    const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId);

    return useQuery<number>({
        queryKey: ["league-accreditation-cutoff-hours", currentLeagueId],
        queryFn: async () => {
            if (!currentLeagueId) return DEFAULT_ACCREDITATION_CUTOFF_HOURS;
            const { data, error } = await supabase
                .from("leagues")
                .select("accreditation_cutoff_hours")
                .eq("id", currentLeagueId)
                .maybeSingle();

            if (error) throw new Error(error.message);
            return (
                (data?.accreditation_cutoff_hours as number | null) ??
                DEFAULT_ACCREDITATION_CUTOFF_HOURS
            );
        },
        enabled: !!currentLeagueId,
        staleTime: 30 * 60 * 1000,
    });
}

/**
 * Hook to fetch a single accreditation demand by ID, with the full
 * per-journalist breakdown needed by the review modal.
 */
export function useAccreditationDemandById(id: string | null) {
    return useQuery<AccreditationDemand | null>({
        queryKey: ["accreditation-demand", id],
        queryFn: async () => {
            if (!id) return null;

            const { data, error } = await supabase
                .from("accreditation_demands")
                .select(DEMAND_DETAIL_SELECT)
                .eq("id", id)
                .single();

            if (error) {
                if (error.code === "PGRST116") return null; // Not found
                throw new Error(error.message);
            }

            const demand = data as unknown as AccreditationDemand;
            return (await attachReviewerNames([demand]))[0];
        },
        enabled: !!id,
    });
}

/**
 * "x / 2" of one (organization, match): every accreditation request the org
 * holds on the match, across all its demands (the DB cap trigger counts the
 * same rows). Only active statuses are fetched; the cap is fixed server-side
 * by media_match_org_cap().
 */
export function useOrgMatchQuota(
    organizationId: string | null | undefined,
    matchId: string | null | undefined
) {
    return useQuery<PerMatchQuota>({
        queryKey: ["accreditation-org-match-quota", organizationId, matchId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("accreditation_requests")
                .select("id, status")
                .eq("organization_id", organizationId as string)
                .eq("match_id", matchId as string)
                .in("status", [...ACTIVE_REQUEST_STATUSES]);
            if (error) throw new Error(error.message);
            return perMatchQuota((data ?? []) as Array<{ status: string }>);
        },
        enabled: !!organizationId && !!matchId,
        staleTime: 30000,
    });
}

/**
 * Mutation to review an accreditation demand as a unit.
 * Calls the review-accreditation-demand edge function, which applies the
 * approve/reject decision (with optional per-journalist overrides) to each
 * child accreditation_requests row using the same side-effects as
 * review-accreditation-request. The demand's derived status is recomputed
 * server-side by a DB trigger.
 */
export function useReviewAccreditationDemand() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            payload: ReviewDemandPayload
        ): Promise<ReviewDemandResponse> => {
            const { data, error } = await supabase.functions.invoke(
                "review-accreditation-demand",
                {
                    body: payload,
                }
            );

            // Coded errors (INVALID_ZONES, INVALID_VALIDITY_RANGE, ...) so the
            // drawer shows the localized api_errors.<CODE> message.
            if (error) {
                throw await mediaErrorFromInvoke(error);
            }
            const bodyError = mediaErrorFromBody(data);
            if (bodyError) {
                throw bodyError;
            }

            return data as ReviewDemandResponse;
        },
        onSuccess: () => {
            // Demand-spawned child rows are normal accreditation_requests, so
            // the request views and the dashboard stats refresh too
            // (see hooks/mediaInvalidation.ts).
            REVIEW_DEMAND_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}
